// What the composer holds after a conversation loads and after a send
// (#11207, round 2).
//
// On the agentic backend, attachments are conversation context: what a
// message attached is part of the conversation from then on (the server
// unions it onto the conversation's set and the model's document searches
// default to it), so the composer starts empty on reload and empties on
// send, and a later message sent with nothing selected attaches nothing new.
// Keeping the chips in the composer was the legacy chat's rule — its server
// acts only on the selection, so the selection had to persist — and that
// backend keeps it.

import type { ChatBackendKind } from "./selectChatBackend";
import type { ChatMessageDto, SelectedFile } from "./types";
import type { FileSelectionState } from "@/components/common/FileSelectionModal/types";

export interface ComposerSelection {
  files: SelectedFile[];
  folderPaths: string[][];
}

const empty = (): ComposerSelection => ({ files: [], folderPaths: [] });

/** The selection the composer shows when a conversation loads. */
export function restoredComposerSelection(
  kind: ChatBackendKind,
  messages: ChatMessageDto[]
): ComposerSelection {
  if (kind === "agentic") {
    return empty();
  }
  // Legacy: the last user message that carried files.
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (!message.isUser || !message.selectedFiles?.length) {
      continue;
    }
    return {
      files: message.selectedFiles,
      folderPaths: message.selectedFolderPaths || [],
    };
  }
  return empty();
}

/** The selection the composer keeps once a message carrying `current` has
 * been sent. */
export function composerSelectionAfterSend(
  kind: ChatBackendKind,
  current: ComposerSelection
): ComposerSelection {
  return kind === "agentic" ? empty() : current;
}

/** The folders a selection reads as, for chips and manifests: a path whose
 * strict prefix is also selected is the subfolder the picker ticked along
 * with its parent, not a second pick — the user chose the parent once. */
export function topLevelFolderPaths(paths: string[][]): string[][] {
  const keys = paths.map((path) => JSON.stringify(path));
  return paths.filter((path, i) => {
    for (let depth = 1; depth < path.length; depth++) {
      const prefix = JSON.stringify(path.slice(0, depth));
      const j = keys.indexOf(prefix);
      if (j !== -1 && j !== i) {
        return false;
      }
    }
    return true;
  });
}

/** The files that get a chip of their own: the ones the user picked by hand.
 * A folder pick's files are the folder's contents, shown as the folder. */
export function handPickedFiles(files: SelectedFile[]): SelectedFile[] {
  return files.filter((file) => !file.viaFolder);
}

/** The composer's files from a picker confirmation: everything the
 * selection expanded to, with the files the user ticked themselves (the
 * picker's raw picks) told apart from the ones a folder brought. A file the
 * endpoint attributed to a picked folder is the folder's even when the raw
 * pick list names it too — the picker's tree cascades a folder tick onto
 * whichever of its files are loaded. Shared by the starter and the
 * conversation pickers so the two cannot drift. */
export function filesFromPickerState(
  state: FileSelectionState
): SelectedFile[] {
  const pickedByHand = new Set(state.selectedFileIds);
  return state.expandedFiles.map((file) => {
    const viaFolder =
      Boolean(file.folderPath) || !pickedByHand.has(file.fileId);
    return {
      id: file.fileId,
      name: file.fileName,
      isFile: true,
      selectedBy: "user" as const,
      viaFolder,
      ...(viaFolder && file.folderPath ? { folderPath: file.folderPath } : {}),
      ...(file.processingFailed ? { processingFailed: true } : {}),
    };
  });
}

/** An upload the picker can show as a picked document: its answer gave it
 * its blob (`blobId`, never a chip's temporary id) and the document is ready.
 * One still processing is left out: the picker cannot pick a document that
 * is processing (its expansion leaves it out and only counts it), so showing
 * it ticked would offer a selection the picker then refuses. */
const pickerCanShowUpload = (file: SelectedFile): boolean =>
  file.blobId !== undefined && file.uploadStatus === "ready";

/** The staged files the picker opens with ticked (#13840): the documents
 * picked from it and the ready uploads of this session, so reopening the
 * picker shows them as staged. */
export function pickerInitialFileIds(files: SelectedFile[]): string[] {
  return files
    .filter((f) => f.isFile && (!f.uploadStatus || pickerCanShowUpload(f)))
    .map((f) => f.id);
}

/** The composer's files once the picker confirms (#13840). The picker's
 * selection stands for the documents it lists; a file uploaded in this
 * session stays staged unless the user unticked it there.
 *
 * Unticked is read from the picker's raw ticks (`selectedFileIds`), never
 * from what the selection expanded to: the expansion leaves out a document
 * still processing, which would read as an untick. `shownFileIds` is what
 * the picker was opened with ticked (`pickerInitialFileIds` of the chips
 * when it opened): an upload in it whose tick is gone goes. An upload the
 * picker was never shown (processing, uploading or failed when it opened, or
 * dropped while it was open) stays as it is, even if it has since finished.
 * An upload still ticked keeps its own chip, which carries its upload state.
 * Shared by the starter and the conversation pickers. */
export function mergePickerConfirm(
  current: SelectedFile[],
  state: FileSelectionState,
  shownFileIds: string[]
): SelectedFile[] {
  const shown = new Set(shownFileIds);
  const ticked = new Set(state.selectedFileIds);
  const keptUploads = current.filter(
    (f) => f.uploadStatus && (!shown.has(f.id) || ticked.has(f.id))
  );
  const keptIds = new Set(keptUploads.map((f) => f.id));
  const picked = filesFromPickerState(state);
  return [...keptUploads, ...picked.filter((f) => !keptIds.has(f.id))];
}

/** The picker's folder selections in the encoding every folder path on
 * this surface uses — the drive id first when the selection named one.
 * Shared so the two pickers and `isUnder` cannot drift. */
export function folderPathsFromPickerState(
  state: FileSelectionState
): string[][] {
  return state.selectedFolderSelections.map((fs) =>
    fs.driveId ? [fs.driveId, ...fs.path] : fs.path
  );
}

/** The send request's attachment fields (#11207), shared by chat and the
 * named agents (#12280) so the two products post one shape. A selected
 * file carries its blob id on `id` (filesystem pick) or `blobId` (upload).
 * `attached_blob_ids` is always present, an empty list included; the folder
 * fields only when a folder was picked. */
export interface AttachmentRequestFields {
  attached_blob_ids: string[];
  attached_folder_paths?: string[][];
  attached_folder_blob_ids?: string[];
  attached_blob_folder_paths?: Record<string, string[]>;
}

export function attachmentRequestFields(
  files: SelectedFile[],
  folderPaths: string[][] = []
): AttachmentRequestFields {
  const idOf = (f: SelectedFile): string | undefined => f.blobId ?? f.id;
  const fields: AttachmentRequestFields = {
    attached_blob_ids: files
      .filter((f) => f.isFile)
      .map(idOf)
      .filter((id): id is string => Boolean(id)),
  };
  if (folderPaths.length > 0) {
    fields.attached_folder_paths = folderPaths;
  }
  const viaFolder = files.filter((f) => f.isFile && f.viaFolder);
  const viaFolderIds = viaFolder
    .map(idOf)
    .filter((id): id is string => Boolean(id));
  if (viaFolderIds.length > 0) {
    fields.attached_folder_blob_ids = viaFolderIds;
    // Which folder each came through, when the picker said, so a reload can
    // count each folder's documents.
    const folderOf: Record<string, string[]> = {};
    for (const f of viaFolder) {
      const id = idOf(f);
      if (f.folderPath && id) {
        folderOf[id] = f.folderPath;
      }
    }
    if (Object.keys(folderOf).length > 0) {
      fields.attached_blob_folder_paths = folderOf;
    }
  }
  return fields;
}

/** The folder half of the attachment fields: the folders picked and which
 * pick each document came through. A named agent's run records these with
 * its inputs (#12495), so its Inputs panel can show a picked folder as one
 * row; absent when no folder was picked. */
export type FolderPickFields = Pick<
  AttachmentRequestFields,
  "attached_folder_paths" | "attached_blob_folder_paths"
>;

export function folderPickFields(
  files: SelectedFile[],
  folderPaths: string[][]
): FolderPickFields {
  const { attached_folder_paths, attached_blob_folder_paths } =
    attachmentRequestFields(files, folderPaths);
  return {
    ...(attached_folder_paths ? { attached_folder_paths } : {}),
    ...(attached_blob_folder_paths ? { attached_blob_folder_paths } : {}),
  };
}

const isUnder = (path: string[] | undefined, folder: string[]): boolean =>
  Boolean(path) &&
  path!.length >= folder.length &&
  folder.every((segment, i) => path![i] === segment);

/** How many documents a picked folder holds: the files that came through it
 * (or one of its ticked subfolders). When the files carry no folder of their
 * own — a message recorded before that was known — the count is exact only
 * with a single folder picked; with several there is no telling which is
 * whose, so no count rather than a wrong one. */
export function folderDocumentCount(
  files: SelectedFile[],
  folder: string[],
  folderPaths: string[][]
): number | null {
  // Every folder-expanded file the picker returns carries its folder, so a
  // selection is attributed wholly or not at all; only a message recorded
  // before folders were kept falls to the branch below.
  const attributed = files.filter((f) => f.viaFolder && f.folderPath);
  if (attributed.length > 0) {
    return attributed.filter((f) => isUnder(f.folderPath, folder)).length;
  }
  if (topLevelFolderPaths(folderPaths).length !== 1) {
    return null;
  }
  return files.filter((f) => f.viaFolder).length;
}

/** The selection after a folder chip's remove control: that folder, its
 * ticked subfolders, and every file that came through them are gone; a file
 * picked by hand inside it stays, since the user chose it on its own. When
 * the files carry no folder of their own, the folder's files cannot be told
 * from another folder's, so all files that came through any folder go with
 * the last folder standing — never silently kept while unseen. */
export function selectionWithoutFolder(
  current: ComposerSelection,
  folder: string[]
): ComposerSelection {
  const folderPaths = current.folderPaths.filter((p) => !isUnder(p, folder));
  const attributed = current.files.some((f) => f.viaFolder && f.folderPath);
  const files = current.files.filter((f) => {
    if (!f.viaFolder) return true;
    if (attributed) return !isUnder(f.folderPath, folder);
    return folderPaths.length > 0;
  });
  return { files, folderPaths };
}

/** How many chips a sent message's row shows before "+N more" stands in. */
export const SENT_CHIP_CAP = 8;

export interface SentChipPlan {
  folders: string[][];
  files: SelectedFile[];
  /** Folders and files past the cap, together — what "+N more" counts. */
  hidden: number;
}

/** The sent row's one rule (#11207): folders lead, files follow, up to
 *  SENT_CHIP_CAP chips in all, and whatever is past that — folder or file —
 *  is the card's count. Eight folders are eight chips and no card; a folder
 *  with twenty files is the folder, seven files and "+13 more". */
export function sentChipPlan(
  folderPaths: string[][],
  handPicked: SelectedFile[]
): SentChipPlan {
  const folders = folderPaths.slice(0, SENT_CHIP_CAP);
  const files = handPicked.slice(0, SENT_CHIP_CAP - folders.length);
  return {
    folders,
    files,
    hidden:
      folderPaths.length - folders.length + handPicked.length - files.length,
  };
}
